/** Session and account reads — P-Q001, P-Q002, and the OTP commands. */
import { apiGet, request } from '../client.js';
import { apiRuntime } from '../runtime.js';
export async function fetchAccount() {
    return apiGet('/v1/accounts/me');
}
/**
 * The handle C1 issues and C2 consumes — API_CONTRACT.md §3.1.
 *
 * C2 takes `{ otp_token, code }`, NOT the phone number: the challenge, not the
 * subscriber, is what is being verified, and the server's schema is strict, so
 * a phone number sent here is refused with 422 rather than ignored. The token
 * therefore has to survive between the two calls, and this is where it is kept.
 *
 * It goes in the session store so a web user who reloads between "send code"
 * and "sign in" can still complete the sign-in they started. A store that
 * refuses (private browsing, or a phone's memory-only store being replaced)
 * falls back to this module, which is enough for one uninterrupted attempt.
 */
const CHALLENGE_KEY = 'erm.otp.challenge';
let fallbackChallenge = null;
function rememberChallenge(value) {
    fallbackChallenge = value;
    try {
        apiRuntime().sessionStore.set(CHALLENGE_KEY, JSON.stringify(value));
    }
    catch {
        // The in-module copy is the whole of the record for this attempt.
    }
}
/**
 * The challenge for THIS number, or nothing.
 *
 * The phone is checked because "Change number" leaves the previous challenge
 * behind, and verifying a new number's code against an old number's token
 * would fail as `auth.otp_invalid` — a message that describes the code the
 * user just read correctly off their phone as wrong.
 */
function recallChallenge(phone) {
    let held = fallbackChallenge;
    try {
        const stored = apiRuntime().sessionStore.get(CHALLENGE_KEY);
        if (stored !== null && stored !== '')
            held = JSON.parse(stored);
    }
    catch {
        // Unreadable or unparseable — the in-module copy stands.
    }
    return held !== null && held.phone === phone ? held.otpToken : null;
}
function forgetChallenge() {
    fallbackChallenge = null;
    try {
        apiRuntime().sessionStore.remove(CHALLENGE_KEY);
    }
    catch {
        // Nothing to release.
    }
}
export async function requestOtp(phone) {
    const result = await request('/v1/auth/otp/request', {
        method: 'POST',
        // `purpose` is C1's own field; the server defaults it, and naming it here
        // keeps a login challenge from being reused for anything else later.
        body: { phone_e164: phone, purpose: 'login' },
        skipAuth: true,
    });
    rememberChallenge({ phone, otpToken: result.data.otp_token });
    return result.data;
}
export async function verifyOtp(phone, code) {
    const otpToken = recallChallenge(phone);
    if (otpToken === null) {
        // Nothing to verify against. Saying so beats a 422 the user cannot act on.
        throw new Error('Ask for a new code before signing in.');
    }
    const result = await request('/v1/auth/otp/verify', {
        method: 'POST',
        body: { otp_token: otpToken, code },
        skipAuth: true,
    });
    // One challenge, one session: the server has consumed it either way.
    forgetChallenge();
    return result.data;
}
export async function logoutSession() {
    await request('/v1/auth/logout', { method: 'POST' });
}
export async function updateAccount(update, ifMatch) {
    const result = await request('/v1/accounts/me', {
        method: 'PATCH',
        body: update,
        ifMatch,
    });
    return result.data;
}
//# sourceMappingURL=session.js.map